import { PanelSourceTypes } from 'core/constants/dashboard';
import { AccessType } from 'core/constants/report';
import { PanelDataFetchCriteria, SaveTempDrilldownCriteria } from 'core/models/dashboard';
import { isMailMergeURL } from 'core/utils/dashboard.util';
import API from 'services/api';
import { ObjModel, ReportResponseModel } from 'core/models';
import { batchHelperForDataRequest } from 'redux-v2/dashboard-store/dashboard-store-requests';
import { ILinkedConfig } from 'redux-v2/report-builder/report-builder-store.state';
import { isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import DashboardBuilderService from './dashboard-builder.service';

class DashboardService extends API {
  static baseURL = process.env.REACT_APP_DATA_BASE_URL || '';

  // TODO: we will be getting this from settings api call,for now we will read it from env
  static apiRequestsLimitPerSecond =
    Number(process.env.REACT_APP_DASHBOARD_API_LIMIT) || 3;

  static onGoingApiRequestCount = 0;

  // all the fetch data requests
  static dataRequestsArray: Array<PanelDataFetchCriteria> = [];

  // AbortControllers mapped by panelId for granular control
  static activeRequestsByPanel: Map<string, AbortController> = new Map();

  constructor() {
    super(DashboardService.baseURL);

    // to keep checking the datarequests array
    setInterval(() => {
      if (DashboardService.dataRequestsArray.length > 0) {
        this.processDataRequestsArray();
      }
    }, 500);
  }

  getDashboardList = async () => this.responsify(await this.instance.get('/dashboards/list'));

  deleteDashboard = async (dashboardId: string) => this.responsify(
    await this.instance.delete('dashboards/delete', {
      params: {
        dashboardId,
      },
    }),
  );

   getFiltersList = async (reportIDs: Array<string>) => this.responsify(
     await this.instance.post('reports/fetch-report-filters', reportIDs),
   );

   getDashboardConfig = async (id: string, personalizeHome: boolean) => this.genericResponseWrapper(
     await this.instance.get('/dashboards/config', {
       params: {
         id,
         personaliseLastVisit: personalizeHome,
       },
     }),
   );

  accesscontrol = async (reportId: string, type: AccessType) => this.responsifyGeneric(
    await this.instance.get('access-control/isAccessible', {
      params: {
        id: reportId,
        type,
      },
    }),
  );

  // as long as data requests available and limit hasn't been reached , trigger panel data requests
  processDataRequestsArray = () => {
    while (DashboardService.dataRequestsArray.length > 0 && DashboardService.onGoingApiRequestCount < DashboardService.apiRequestsLimitPerSecond) {
      DashboardService.onGoingApiRequestCount += 1;
      const topDataRequest = DashboardService.dataRequestsArray.shift();
      this.initiatePanelDataRequest(topDataRequest);
    }
  };

  resetDataRequestArray = () => {
    DashboardService.dataRequestsArray = [];
  };

  static abortRequestsForPanels = (panelIds: string[]) => {
    if (!isValidArrayAndNotEmpty(panelIds)) return;

    const panelSet = new Set(panelIds);

    // Abort active HTTP requests for specific panels
    panelSet.forEach((panelId) => {
      const controller = DashboardService.activeRequestsByPanel.get(panelId);
      if (controller) {
        controller.abort();
        DashboardService.activeRequestsByPanel.delete(panelId);
      }
    });

    // Remove queued requests for these panels
    DashboardService.dataRequestsArray = DashboardService.dataRequestsArray.filter(
      (request) => {
        const requestPanelIds = request.Target || [];
        return !requestPanelIds.some((id) => panelSet.has(id));
      },
    );
  };

  initiatePanelDataRequest = async ({
    Target,
    ...criteria
  }: PanelDataFetchCriteria) => {
    const abortController = new AbortController();
    const cleanupControllers = () => Target.forEach((id) => DashboardService.activeRequestsByPanel.delete(id));

    Target.forEach((panelId) => DashboardService.activeRequestsByPanel.set(panelId, abortController));

    try {
      let result;
      const { SourceType, Url, SourceUrl } = criteria;

      if (SourceType === PanelSourceTypes.EmbeddedUrl) {
        const dashboardService = new DashboardBuilderService();
        const isValidUrl = await dashboardService.validateEmbedURL(Url);
        if (!isValidUrl) {
          batchHelperForDataRequest(null, Target, true);
          DashboardService.onGoingApiRequestCount -= 1;
          cleanupControllers();
          return;
        }

        if (isMailMergeURL(SourceUrl)) {
          result = await this.instance.get('users/replace-mail-merge-fields', {
            params: { url: `${SourceUrl}` },
            signal: abortController.signal,
          });
        }
      } else {
        result = await this.instance.post('dashboards/fetch-report-data', criteria, {
          signal: abortController.signal,
        });
      }
      batchHelperForDataRequest(this.responsifyGeneric(result), Target, false);
      DashboardService.onGoingApiRequestCount -= 1;
      cleanupControllers();
    } catch (ex: any) {
      if (ex.name === 'CanceledError' || ex.code === 'ERR_CANCELED') {
        DashboardService.onGoingApiRequestCount -= 1;
        cleanupControllers();
        return;
      }
      batchHelperForDataRequest(null, Target, true, ex?.status);
      DashboardService.onGoingApiRequestCount -= 1;
      cleanupControllers();
    }
  };

  saveTempDrilldownState = async (
    criteria: SaveTempDrilldownCriteria,
  ): Promise<{
    TempReportId: string;
    LinkedReportId?: string;
    LinkedReportsConfig?: ILinkedConfig[];
  }> => this.responsify(
    await this.instance.post('reports/save-temp-drilldown-state', criteria),
  );

  getPanelData = async (criteria: PanelDataFetchCriteria) => {
    DashboardService.dataRequestsArray.push(criteria);
  };

  saveTempView = async (criteria: SaveTempViewCriteria) => {
    this.responsify(
      await this.instance.post('reports/save-temp-view', criteria),
    );
  };

  setPersonalizedDashboard = async (
    criteria: PersonalizedDashboardCriteria,
  ) => {
    this.responsify(
      await this.instance.put(
        'unified-listing/dashboards/personalise-home',
        null,
        {
          params: {
            id: criteria.id,
            home: criteria.home,
          },
        },
      ),
    );
  };
}

export default DashboardService;

export const SingletonDashboardService = (function iife() {
  let dashboardService: any;

  function createDasbhboardService() {
    dashboardService = new DashboardService();
    return dashboardService;
  }

  return {
    getDasbhboardServiceInstance: () => {
      if (!dashboardService) {
        dashboardService = createDasbhboardService();
      }
      return dashboardService;
    },
  };
}());

export interface SaveTempViewCriteria {
  SourceId: string;
  Filters: any;
  DataSourceId: string;
  DataSourceType: PanelSourceTypes;
  FiltersMetadata?: ReportResponseModel.IFiltersMetaData;
}

export interface PersonalizedDashboardCriteria {
  id: string;
  home: boolean;
}
